import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { NumberInput } from "components/forms";
import {
  COLOR_ERROR,
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  COLOR_STROKE_DIVIDER,
  COLOR_STROKE_IDLE,
  COLOR_VALUE
} from "components/Portfolio-construction/colors";
import { RoleName } from "constants/index";
import { useActionHook } from "hooks";
import { IApiServiceError, usePortfolioConstructionActions, useUserRoleSelector } from "store";
import { ApiValidationErrors, IObjectiveDefaults, ObjectiveConstraintKey } from "store/types";

// Field config — keep `key` exactly as sent on the wire. `max` mirrors the
// server-side per-field ceiling (target-return caps at 25, the rest at 100).
interface FieldConfig {
  key: ObjectiveConstraintKey;
  label: string;
  max: number;
}

const FIELDS: FieldConfig[] = [
  { key: "target-return", label: "Target annualised return", max: 25 },
  { key: "max-vol", label: "Maximum volatility", max: 100 },
  { key: "max-drawdown", label: "Maximum drawdown", max: 100 },
  { key: "liquidity-floor", label: "Liquidity floor", max: 100 }
];

type ValueMap = Record<ObjectiveConstraintKey, string>;
type ErrorMap = Partial<Record<ObjectiveConstraintKey, string>>;

// Inputs hold strings while editing; numbers are rendered without trailing zeros.
const formatValue = (n: number | undefined | null): string =>
  n === undefined || n === null || !Number.isFinite(n) ? "" : String(n);

// Read-only display of a committed value, e.g. "10.00%".
const formatPercent = (raw: string): string => {
  const num = Number(raw);
  if (raw.trim() === "" || !Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 2 }).format(num / 100);
};

const toValueMap = (defaults: IObjectiveDefaults): ValueMap =>
  FIELDS.reduce((acc, field) => {
    acc[field.key] = formatValue(defaults[field.key]);
    return acc;
  }, {} as ValueMap);

// Client-side validation, mirroring the server: required, numeric, >= 0,
// <= max, at most 2 decimals. Returns undefined when the value is valid.
const validateField = (field: FieldConfig, raw: string): string | undefined => {
  const trimmed = raw.trim();
  if (trimmed === "") return "Required.";
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return "Enter a valid number.";
  if (num < 0) return "Must be 0 or more.";
  if (num > field.max) return `May not exceed ${field.max}.`;
  const decimals = trimmed.split(".")[1];
  if (decimals && decimals.length > 2) return "Use at most 2 decimal places.";
  return undefined;
};

// Map a server validation error (keyed `defaults.<constraint-key>`) to a field.
const messageForField = (errors: ApiValidationErrors, key: ObjectiveConstraintKey): string | undefined => {
  const entry = errors[`defaults.${key}`] ?? Object.entries(errors).find(([field]) => field.includes(key))?.[1];
  if (!entry) return undefined;
  return Array.isArray(entry) ? entry.join(" ") : String(entry);
};

export const ObjectiveDefaults: React.FC = () => {
  const { getObjectiveDefaultsAction, updateObjectiveDefaultsAction } = usePortfolioConstructionActions();
  const { name: role } = useUserRoleSelector() || {};
  const isAdmin = role === RoleName.Admin;

  // `loaded` is the last persisted set — the baseline for "changed" detection and Cancel.
  const [loaded, setLoaded] = useState<IObjectiveDefaults | null>(null);
  const [values, setValues] = useState<ValueMap>({} as ValueMap);
  const [errors, setErrors] = useState<ErrorMap>({});

  const applyLoaded = (defaults: IObjectiveDefaults) => {
    setLoaded(defaults);
    setValues(toValueMap(defaults));
    setErrors({});
  };

  const { loading: loadingDefaults, action: loadDefaults } = useActionHook(getObjectiveDefaultsAction, {
    onSuccess: applyLoaded,
    onReject: () => {
      toast.error("Could not load objective defaults. Try again.");
    }
  });

  const { loading: saving, action: saveDefaults } = useActionHook(updateObjectiveDefaultsAction, {
    onSuccess: (updated) => {
      applyLoaded(updated);
      toast.success("Defaults saved");
    }
  });

  useEffect(() => {
    loadDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the draft value and clear any error for the field while typing.
  const handleChange = (field: FieldConfig, raw: string) => {
    setValues((prev) => ({ ...prev, [field.key]: raw }));
    setErrors((prev) => {
      if (!(field.key in prev)) return prev;
      const next = { ...prev };
      delete next[field.key];
      return next;
    });
  };

  // Validate the field once the user leaves it.
  const handleBlur = (field: FieldConfig) => {
    setErrors((prev) => {
      const next = { ...prev };
      const error = validateField(field, values[field.key] ?? "");
      if (error) next[field.key] = error;
      else delete next[field.key];
      return next;
    });
  };

  const handleCancel = () => {
    if (loaded) applyLoaded(loaded);
  };

  const handleSave = async () => {
    const nextErrors: ErrorMap = {};
    FIELDS.forEach((field) => {
      const error = validateField(field, values[field.key] ?? "");
      if (error) nextErrors[field.key] = error;
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const defaults = FIELDS.reduce(
      (acc, field) => {
        acc[field.key] = Number(values[field.key]);
        return acc;
      },
      {} as Record<ObjectiveConstraintKey, number>
    );

    try {
      await saveDefaults({ defaults });
    } catch (e) {
      const apiError = e as IApiServiceError;
      const fieldErrors = apiError?.errors;
      if (apiError?.status === 422 && fieldErrors && Object.keys(fieldErrors).length > 0) {
        const mapped: ErrorMap = {};
        FIELDS.forEach((field) => {
          const message = messageForField(fieldErrors, field.key);
          if (message) mapped[field.key] = message;
        });
        if (Object.keys(mapped).length > 0) {
          setErrors(mapped);
          return;
        }
      }
      toast.error(apiError?.message || "Could not save defaults. Try again.");
    }
  };

  const isValid = useMemo(() => FIELDS.every((field) => !validateField(field, values[field.key] ?? "")), [values]);

  const hasChanges = useMemo(() => {
    if (!loaded) return false;
    return FIELDS.some((field) => Number(values[field.key]) !== loaded[field.key]);
  }, [values, loaded]);

  const canSave = isAdmin && hasChanges && isValid && !saving;

  return (
    <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={loadingDefaults && !loaded}>
      <Stack sx={{ flexGrow: 1, overflow: "auto", p: "4px" }}>
        <Stack
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
            overflow: "hidden"
          }}
        >
          {/* Header */}
          <Stack sx={{ padding: "16px 20px", gap: "8px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
              <Typography sx={{ fontSize: "18px", fontWeight: 600, lineHeight: "24px", color: COLOR_PRIMARY }}>
                Objective defaults
              </Typography>
              {!isAdmin && (
                <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: COLOR_SECONDARY }}>Read only</Typography>
              )}
            </Stack>
            <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: COLOR_SECONDARY }}>
              These values apply to every investor and slice that hasn&apos;t set its own objective. Changing a default
              updates the baseline for all of them — it does not overwrite values that have been set manually.
            </Typography>
          </Stack>

          {/* Fields */}
          <Stack sx={{ borderTop: `1px solid ${COLOR_STROKE_DIVIDER}` }}>
            {FIELDS.map((field) => {
              const error = errors[field.key];
              return (
                <Stack
                  key={field.key}
                  sx={{
                    borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
                    "&:last-of-type": { borderBottom: "none" }
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="16px"
                    sx={{ minHeight: "56px", padding: "10px 20px" }}
                  >
                    <Typography
                      sx={{ flex: "1 1 0", minWidth: 0, fontSize: "14px", lineHeight: "20px", color: COLOR_PRIMARY }}
                    >
                      {field.label}
                    </Typography>

                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: COLOR_SECONDARY,
                        textAlign: "right"
                      }}
                    >
                      max {field.max}
                    </Typography>

                    <Box sx={{ width: 140, flexShrink: 0 }}>
                      {isAdmin ? (
                        <NumberInput
                          name={field.key}
                          value={values[field.key] ?? ""}
                          onChange={(e) => handleChange(field, (e.target as HTMLInputElement).value)}
                          onBlur={() => handleBlur(field)}
                          disabled={saving}
                          error={!!error}
                          size="small"
                          fullWidth
                          numberInputProps={{ decimalScale: 2, allowNegative: false }}
                          InputProps={{
                            endAdornment: (
                              <Typography
                                sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "20px", ml: "4px" }}
                              >
                                %
                              </Typography>
                            ),
                            sx: {
                              fontSize: "15px",
                              fontWeight: 600,
                              color: COLOR_PRIMARY,
                              "& input": { textAlign: "right", padding: "8px 10px" },
                              "& fieldset": { borderColor: COLOR_STROKE_IDLE }
                            }
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: COLOR_VALUE,
                            textAlign: "right",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {formatPercent(values[field.key] ?? "")}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  {error && (
                    <Typography
                      sx={{ color: COLOR_ERROR, fontSize: "11px", lineHeight: "14px", padding: "0 20px 8px" }}
                    >
                      {error}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>

          {/* Footer */}
          {isAdmin && (
            <Stack
              direction="row"
              gap="10px"
              justifyContent="flex-end"
              sx={{ padding: "16px 20px", borderTop: `1px solid ${COLOR_STROKE_DIVIDER}` }}
            >
              <Button variant="outlined" onClick={handleCancel} disabled={!hasChanges || saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!canSave}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Save
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </AsyncContentMui>
  );
};
